import { Box, Button, Grid, Link, Stack, SvgIcon, TextField, useMediaQuery, useTheme } from '@mui/material';
import { Plus, SearchSm } from '../../../../assets';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { UserContext } from '../../../../components/shared/useUser';
import { Section } from '../../../../components/ui/Section';
import useLogError from '../../../../hooks/useLogError';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { AutoshipTemplateModel, client, OrderType } from '../../../../app/services/api/orderManagementClient';
import AutoshipmentScheduleTable from './AutoshipmentScheduleTable';
import { AutoshipScheduleModel } from '../../../../app/services/api/orderManagementClient';
import { AutoshipmentSchedulesFilterOptions } from '../../../../app/services/api/orderManagementClient';
import { PaginatedList } from '../../../../app/services/api/orderManagementClient';
import CustomBreadCrumbs from '../../../../components/ui/CustomBreadCrumbs';
import { AutoshipTemplateStatusEnum } from '../../enums/AutoshipmentTemplateStatusEnum';
import LoadingWheel from '../../../../components/ui/LoadingWheel';

const AutoshipmentSchedules = () => {
  const [paginatedData, setPaginatedData] = useState<PaginatedList<AutoshipScheduleModel>>();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('createdDate');
  const theme = useTheme();
  const { t } = useTranslation('autoshipment');
  const { hasPermissionTo, isAdmin, selectedStore } = useContext(UserContext);
  const hasWritePermissions = hasPermissionTo(['AutoshipmentWrite']) || isAdmin();
  const hasDeletePermissions = hasPermissionTo(['AutoshipmentDelete']);
  const navigate = useNavigate();
  const { logError } = useLogError();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [scheduleSearch, setScheduleSearch] = useState('');
  const defaultSort = 'id';
  const defaultDirection = 'desc';
  const defaultPageSize = 10;
  const { id } = useParams<{ id: string | undefined }>();
  const templateId = id ? parseInt(id, 10) : 0;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [templateDetails, setTemplateDetails] = useState<AutoshipTemplateModel | null>(null);

  const handleSnackbarOpen = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (paginatedData !== undefined) {
      loadFilterData({
        name: scheduleSearch,
        templateId: templateId,
        sort: orderBy || defaultSort,
        direction: order || defaultDirection,
        pageNumber: page,
        pageSize: defaultPageSize,
      });
    }
  }, [page, order, orderBy]); 

  useEffect(() => {
    setIsLoading(true);
    loadData();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const loadData = async () => {
    const autoshipTemplateRes = await client.getAutoshipTemplateById(id ?? '', selectedStore?.storeNumber ?? '', true);
    setTemplateDetails(autoshipTemplateRes.data);

    await fetchOrderTypes();
    await loadFilterData({
      name: scheduleSearch,
      templateId: templateId,
      sort: defaultSort,
      direction: defaultDirection,
      pageNumber: page,
      pageSize: defaultPageSize,
    });
    setIsLoading(false);
  };

  const fetchOrderTypes = async () => {
    try {
      const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
      setOrderTypes(orderTypesResponse.data);
    } catch (error) {
      console.error(t('errorLoading'), error);
    }
  };

  const loadFilterData = async (options: AutoshipmentSchedulesFilterOptions) => {
    setIsLoading(true);
    try {
      const result = await client.getAutoshipSchedules(options, selectedStore?.storeNumber ?? '');

      if (result.success) {
        setPaginatedData(result.data);
      } else {
        logError(result.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const callLoadDataFromAPI = (options: AutoshipmentSchedulesFilterOptions) => {
    loadFilterData(options);
  };

  const clearAll = () => {
    setScheduleSearch('');
    setPage(0);

    callLoadDataFromAPI({
      name: '',
      templateId: templateId,
      sort: defaultSort,
      direction: defaultDirection,
      pageNumber: 0,
      pageSize: 10,
    } as AutoshipmentSchedulesFilterOptions);
  };

  const searchClick = () => {
    setPage(0);

    callLoadDataFromAPI({
      name: scheduleSearch,
      templateId: templateId,
      sort: defaultSort,
      direction: defaultDirection,
      pageNumber: 0,
      pageSize: defaultPageSize,
    });
  };

  const navigateToSchedule = (id: number) => {
    navigate(`/manage-autoshipments/schedule/${id}`);
  };

  const handleDelete = async (id: number) => {
    if(!hasDeletePermissions) {
      return;
    }
    try {
      setIsLoading(true);
      const result = await client.deleteSchedule(selectedStore?.storeNumber ?? '', id?.toString());
      
      if (result.success) {
        handleSnackbarOpen(t('scheduleDeleted'), 'success');
        await loadData();
      } else {
        logError(result.message);
        handleSnackbarOpen(t('scheduleDeleteFailed'), 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('scheduleDeleteFailed'), 'error');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <PageArea>
      <Box>
        <CustomBreadCrumbs title={t('templateScheduleNav')}>
          <Link to={'/manage-autoshipments/templates'}
            component={RouterLink}>{ t('templatesnav') }</Link>
        </CustomBreadCrumbs>
      </Box>
      <Box display="flex"
        width="100%"
        height="100%"
        flexDirection="column" >
        <Grid container>
          <Stack width='100%'
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}>
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle={t('schedulesSubTitle')}>
                {t('schedulesTitle')}
              </PageTitle>
            </Box>
            {hasWritePermissions && templateDetails?.status == Number(AutoshipTemplateStatusEnum.ACTIVE) && (
              <Box width={isMobile ? '100%' : 'auto'}
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                }}>
                <Button
                  variant="primary"
                  size="lg"
                  sx={{
                    mb: { xs: 4, md: 0 },
                  }}
                  onClick={() => navigate(`/manage-autoshipments/schedule/new/${templateId}`)}
                  startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
                >
                  {t('newScheduleBtn')}
                </Button>
              </Box>
            )}
          </Stack>

          <Grid container
            sx={{ pt: '6px', pb: '12px' }}
            spacing={4}>
            <Grid item
              xs={12}
              sm={6}
              md={3}>
              <TextField
                fullWidth
                size='small'
                label={t('searchSchedules')}
                value={scheduleSearch}
                onChange={(e) => setScheduleSearch(e.target.value)}
              />
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}>
              <Button
                variant="secondary"
                size="lg"
                onClick={clearAll}
                fullWidth
              >
                {t('clear')}
              </Button>
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right' }}>
              <Button
                variant="primary"
                size="lg"
                startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                onClick={searchClick}
                fullWidth>
                {t('search')}
              </Button>
            </Grid>
          </Grid>

          <Grid item
            xs={12}
            sx={{ mt: '12px' }}>
            {!paginatedData || isLoading ? <LoadingWheel /> :
              <Section>
                {
                  <AutoshipmentScheduleTable
                    templateStatus={templateDetails?.status}
                    templateId={templateId}
                    list={paginatedData}
                    page={page}
                    setPage={setPage}
                    order={order}
                    setOrder={setOrder}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                    clearAll={clearAll}
                    isLoading={isLoading}
                    navigateToSchedule={navigateToSchedule}
                    orderTypes={orderTypes}
                    handleDelete={handleDelete}
                    hasWritePermissions={hasWritePermissions}
                    hasDeletePermissions={hasDeletePermissions}                />
                }
              </Section >
            }
          </Grid>
        </Grid >
      </Box >
      <MessagesSnackbar 
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
        duration={5000} 
      />
    </PageArea >
  );
};

export default AutoshipmentSchedules;